import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Home as HomeIcon,
  Loader2,
  RotateCw,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { isTauri } from "../lib/local-media.js";

/**
 * Vollwertige In-App-Browser-Seite.
 *
 * Zwei Render-Pfade je nach Runtime:
 *
 * 1. **Tauri Desktop**: erzeugt eine echte Tauri-2 Child-`Webview` und
 *    positioniert sie über dem Inhaltsbereich. Damit funktionieren auch
 *    Seiten, die per `X-Frame-Options` Embedding verbieten (Google,
 *    YouTube-Login, Banken …). Voraussetzung: `unstable`-Feature im
 *    `src-tauri/Cargo.toml` (siehe Datei) und die Webview-Permissions
 *    in `src-tauri/capabilities/default.json`. Wenn die API noch nicht
 *    verfügbar ist, fällt der Code stillschweigend auf das Iframe zurück.
 *
 * 2. **Web/PWA und Tauri Mobile**: Iframe-Fallback. Viele Seiten lassen
 *    sich dort nicht einbetten — in dem Fall blendet der `BlockOverlay`
 *    einen Hinweis ein.
 */
const HOME_URL = "https://www.google.com/";

export function BrowserPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Optional ?url=… damit andere Stellen der App direkt mit einem Ziel
  // einsteigen können (z. B. „Spielerprofil ansehen").
  const initial = searchParams.get("url") || HOME_URL;

  const [currentUrl, setCurrentUrl] = useState(initial);
  const [addressInput, setAddressInput] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // History wird clientseitig geführt — Cross-Origin-Iframes geben uns
  // keinen Zugriff auf `iframe.contentWindow.history`, deshalb tracken
  // wir Vor/Zurück selbst pro Adresseingabe.
  const historyRef = useRef<string[]>([initial]);
  const historyIdxRef = useRef<number>(0);
  const [, forceTick] = useState(0);

  const pushHistory = useCallback((url: string) => {
    const arr = historyRef.current.slice(0, historyIdxRef.current + 1);
    arr.push(url);
    historyRef.current = arr;
    historyIdxRef.current = arr.length - 1;
    forceTick((n) => n + 1);
  }, []);

  const navigateTo = useCallback(
    (raw: string, opts?: { pushHistory?: boolean }) => {
      const url = normalizeAddress(raw);
      if (!url) return;
      setCurrentUrl(url);
      setAddressInput(url);
      setBlocked(false);
      setLoading(true);
      if (opts?.pushHistory !== false) pushHistory(url);
    },
    [pushHistory],
  );

  function goBack() {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current -= 1;
    const url = historyRef.current[historyIdxRef.current]!;
    setCurrentUrl(url);
    setAddressInput(url);
    setLoading(true);
    setBlocked(false);
    forceTick((n) => n + 1);
  }

  function goForward() {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current += 1;
    const url = historyRef.current[historyIdxRef.current]!;
    setCurrentUrl(url);
    setAddressInput(url);
    setLoading(true);
    setBlocked(false);
    forceTick((n) => n + 1);
  }

  function reload() {
    setLoading(true);
    setBlocked(false);
    setReloadKey((k) => k + 1);
  }

  const canBack = historyIdxRef.current > 0;
  const canForward = historyIdxRef.current < historyRef.current.length - 1;

  return (
    <div className="flex h-svh flex-col bg-background">
      {/* ── Browser-Chrome (native action bar) ───────────────── */}
      <header
        className="flex shrink-0 items-center gap-1.5 border-b px-3 py-2 sm:gap-2 sm:px-4"
        style={{
          borderColor: "rgba(255,255,255,0.08)",
          background: "rgba(20,24,24,0.85)",
          backdropFilter: "blur(12px)",
        }}
      >
        <ChromeButton label="Zurück zur App" onClick={() => navigate(-1)} tone="muted">
          <X className="size-4" />
        </ChromeButton>

        <div className="mx-1 h-5 w-px bg-white/10" aria-hidden />

        <ChromeButton label="Seite zurück" onClick={goBack} disabled={!canBack}>
          <ArrowLeft className="size-4" />
        </ChromeButton>
        <ChromeButton label="Seite vor" onClick={goForward} disabled={!canForward}>
          <ArrowRight className="size-4" />
        </ChromeButton>
        <ChromeButton label="Neu laden" onClick={reload}>
          <RotateCw className="size-4" />
        </ChromeButton>
        <ChromeButton label="Startseite" onClick={() => navigateTo(HOME_URL)}>
          <HomeIcon className="size-4" />
        </ChromeButton>

        <AddressBar
          value={addressInput}
          loading={loading}
          onChange={setAddressInput}
          onSubmit={() => navigateTo(addressInput)}
        />

        <ChromeButton label="Extern öffnen" onClick={() => openExternally(currentUrl)}>
          <ExternalLink className="size-4" />
        </ChromeButton>
      </header>

      {/* ── Viewport: entweder Tauri-Child-Webview oder Iframe ── */}
      <div className="relative min-h-0 flex-1 bg-white">
        <BrowserViewport
          url={currentUrl}
          reloadKey={reloadKey}
          onLoad={() => {
            setLoading(false);
            setBlocked(false);
          }}
          onBlocked={() => {
            setLoading(false);
            setBlocked(true);
          }}
        />
        {blocked && (
          <BlockOverlay
            url={currentUrl}
            onRetry={reload}
            onExternal={() => openExternally(currentUrl)}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── Viewport ─────────────────────────── */

const BLOCK_TIMEOUT_MS = 4500;

/**
 * Versucht zunächst, eine echte Tauri-Child-Webview zu spawnen. Klappt
 * das nicht (kein Tauri, fehlende Permissions, kein `unstable`-Feature),
 * fällt die Komponente auf ein Iframe zurück.
 */
function BrowserViewport({
  url,
  reloadKey,
  onLoad,
  onBlocked,
}: {
  url: string;
  reloadKey: number;
  onLoad: () => void;
  onBlocked: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // `mode` startet als "probing"; sobald wir wissen ob Tauri-Webview
  // möglich ist, schalten wir um. Bei jedem URL/Reload-Wechsel wird die
  // Child-Webview komplett neu erzeugt — die Tauri-API exponiert keinen
  // `setUrl`/`eval`-Endpunkt für Child-Webviews, also ist Recreate der
  // sauberste Weg (clearAllBrowsingData() bleibt dem User vorbehalten).
  const [mode, setMode] = useState<"probing" | "tauri" | "iframe">(() =>
    isTauri() ? "probing" : "iframe",
  );

  // Tauri-Pfad: Child-Webview lifecycle managen.
  useEffect(() => {
    if (mode === "iframe") return;
    if (!containerRef.current) return;

    let disposed = false;
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const [{ Webview }, { getCurrentWindow }, dpiMod] = await Promise.all([
          import("@tauri-apps/api/webview"),
          import("@tauri-apps/api/window"),
          import("@tauri-apps/api/dpi"),
        ]);
        if (disposed) return;
        const win = getCurrentWindow();
        const label = `iab-${Date.now()}`;
        const rect = containerRef.current!.getBoundingClientRect();
        const wv = new Webview(win, label, {
          url,
          x: Math.round(rect.x),
          y: Math.round(rect.y),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });

        await wv.once("tauri://created", () => {
          if (disposed) return;
          setMode("tauri");
          onLoad();
        });
        wv.once("tauri://error", (e) => {
          if (disposed) return;
          // eslint-disable-next-line no-console
          console.warn("[browser] tauri webview error", e);
          setMode("iframe");
        });

        // Bounds-Sync: jede Größen-/Scroll-Änderung des Containers an die
        // native Webview weiterreichen, damit sie passend „unter" dem
        // React-Slot sitzt.
        const syncBounds = async () => {
          if (disposed || !containerRef.current) return;
          const r = containerRef.current.getBoundingClientRect();
          try {
            await wv.setPosition(new dpiMod.LogicalPosition(Math.round(r.x), Math.round(r.y)));
            await wv.setSize(new dpiMod.LogicalSize(Math.round(r.width), Math.round(r.height)));
          } catch {
            /* webview wurde wahrscheinlich gerade geschlossen */
          }
        };
        const ro = new ResizeObserver(syncBounds);
        ro.observe(containerRef.current!);
        window.addEventListener("resize", syncBounds);
        window.addEventListener("scroll", syncBounds, true);

        cleanup = () => {
          ro.disconnect();
          window.removeEventListener("resize", syncBounds);
          window.removeEventListener("scroll", syncBounds, true);
          wv.close().catch(() => {});
        };
      } catch (err) {
        if (disposed) return;
        // eslint-disable-next-line no-console
        console.warn("[browser] Tauri Child-Webview nicht verfügbar, Iframe-Fallback aktiv", err);
        setMode("iframe");
      }
    })();

    return () => {
      disposed = true;
      cleanup?.();
    };
    // URL/Reload-Wechsel → Webview neu erzeugen. Das ist nicht so
    // elegant wie eine in-place-Navigation, aber die einzige Option im
    // aktuellen Tauri-API-Surface.
  }, [url, reloadKey, mode, onLoad]);

  // Iframe-Fallback Block-Heuristik
  useEffect(() => {
    if (mode !== "iframe") return;
    const t = window.setTimeout(onBlocked, BLOCK_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [url, reloadKey, mode, onBlocked]);

  return (
    <div ref={containerRef} className="absolute inset-0 bg-white">
      {mode === "iframe" && (
        <iframe
          key={`${url}::${reloadKey}`}
          src={url}
          title="Browser"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
          referrerPolicy="no-referrer-when-downgrade"
          allow="autoplay; clipboard-read; clipboard-write; fullscreen; geolocation; microphone; camera"
          onLoad={onLoad}
          className="size-full border-0"
        />
      )}
      {mode === "probing" && (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
      {/* Tauri-Webview liegt nativ über dem Container — kein DOM-Content. */}
    </div>
  );
}

/* ─────────────────────────── Address bar ────────────────────────── */

function AddressBar({
  value,
  loading,
  onChange,
  onSubmit,
}: {
  value: string;
  loading: boolean;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="relative mx-1 flex min-w-0 flex-1 items-center"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <span
        className="pointer-events-none absolute left-3 grid size-4 place-items-center"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        {loading ? <Loader2 className="size-3.5 animate-spin" /> : <Search className="size-3.5" />}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="URL oder Suchbegriff…"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        className="w-full rounded-xl py-1.5 pl-9 pr-3 text-[13px] text-white outline-none transition-colors focus:bg-black/50"
        style={{
          background: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      />
    </form>
  );
}

/* ─────────────────────────── Block overlay ──────────────────────── */

function BlockOverlay({
  url,
  onRetry,
  onExternal,
}: {
  url: string;
  onRetry: () => void;
  onExternal: () => void;
}) {
  const tauri = isTauri();
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-6 backdrop-blur-md">
      <div className="glass-floating max-w-md rounded-2xl p-6 text-center">
        <ShieldAlert
          className="mx-auto mb-3 size-8"
          style={{ color: "var(--color-tactical-red)" }}
        />
        <h3 className="mb-1 text-title-md font-semibold">Diese Seite blockiert Einbettung</h3>
        <p className="mb-4 break-all text-body-sm text-white/70">
          {hostnameOf(url) ?? url} erlaubt kein Embedding.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={onExternal}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold"
            style={{
              background: "var(--color-pitch-lime)",
              color: "#1d3700",
            }}
          >
            <ExternalLink className="size-4" />
            {tauri ? "In Tauri-Fenster öffnen" : "Im Browser öffnen"}
          </button>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white/80 hover:text-white"
            style={{ border: "1px solid rgba(255,255,255,0.18)" }}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Chrome button ──────────────────────── */

function ChromeButton({
  children,
  label,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "muted";
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-9 place-items-center rounded-lg text-white/85 transition-colors hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
      style={tone === "muted" ? { color: "var(--color-on-surface-variant)" } : undefined}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── Helpers ──────────────────────────── */

/**
 * Adressleisten-Eingabe interpretieren:
 * - Sieht aus wie URL → direkt verwenden (https:// ergänzt wenn nötig)
 * - Sonst → Google-Suche
 */
function normalizeAddress(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // bereits volle URL?
  try {
    const u = new URL(trimmed);
    if (u.protocol === "http:" || u.protocol === "https:") return u.toString();
    return null;
  } catch {
    /* not a URL yet */
  }
  // Domain-Heuristik (enthält einen Punkt, keine Leerzeichen)
  if (/^[^\s]+\.[^\s]+$/.test(trimmed) && !trimmed.includes(" ")) {
    try {
      return new URL(`https://${trimmed}`).toString();
    } catch {
      /* fall through */
    }
  }
  // Sonst Google-Suche
  return `https://www.google.com/search?q=${encodeURIComponent(trimmed)}`;
}

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

async function openExternally(url: string): Promise<void> {
  if (isTauri()) {
    try {
      const mod = await import("@tauri-apps/api/webviewWindow");
      new mod.WebviewWindow(`iab-window-${Date.now()}`, {
        url,
        title: hostnameOf(url) ?? url,
        width: 1100,
        height: 800,
        resizable: true,
        focus: true,
      });
      return;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[browser] WebviewWindow fallback", err);
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
